<template>
  <base-linear-loader v-if="loading" class="bg-tonedBackground" />
  <v-toolbar
    v-if="showToolbar"
    flat
    color="primary"
    dark
    :height="toolbarHeight"
  >
    <v-btn
      v-if="backLocation"
      v-set-test-id="[componentName, testId, 'back-button']"
      :to="backLocation"
      icon
      @click="clickBack"
    >
      <font-awesome-icon :icon="faArrowLeft" size="lg" />
    </v-btn>
    <v-toolbar-title>{{ title }}</v-toolbar-title>
    <v-spacer v-if="slots['right']" />
    <slot name="right" />
  </v-toolbar>
  <v-tabs
    v-if="tabs && tabs.length"
    bg-color="primary"
    dark
    show-arrows
    :height="tabsHeight"
  >
    <v-tab
      v-for="(tab, index) in tabs"
      :key="tab.id || index"
      v-set-test-id:a="[componentName, testId, tab.testId || tab.to]"
      :to="tab.to"
      :exact="tab.exact"
      :target="tab.target"
      :disabled="tab.disabled"
      @click="$emit('pressed-tab', tab)"
    >
      {{ tab.title }}
    </v-tab>
  </v-tabs>
</template>

<script lang="ts" setup>
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { computed, useSlots } from 'vue';
import type { ZvdZLocationRaw } from '@/plugins/router';
import { previousRoute, router } from '@/plugins/router';
import type { ActionableComponentProps } from '@/common/components/ActionableComponent';
import { vSetTestId } from '@/common/components/ActionableComponent';
import type { TabOptions } from '@/common/components/zvdzo/TheTitleBar.vue';

const componentName = 'BaseTitleBar';

type Props = ActionableComponentProps & {
  title?: string;
  tabs?: TabOptions[];
  backLocation?: ZvdZLocationRaw;
  loading?: boolean;
};
const props = defineProps<Props>();
const slots = useSlots();
type Emits = { (e: 'pressed-tab', tab: TabOptions): void };
defineEmits<Emits>();

const showToolbar = computed<boolean>(
  () => Boolean(props.backLocation) || Boolean(props.title),
);

const tabsHeight = computed<number | undefined>(() =>
  showToolbar.value ? undefined : 80,
);

const toolbarHeight = computed(() => (!props.tabs?.length ? 81 : 49));

const backLocationEqualsHistoryBack = computed<boolean>(() => {
  if (!props.backLocation) {
    return false;
  }
  const backRoute = router.resolve(props.backLocation);
  return backRoute.name === previousRoute.value?.name;
});

const clickBack = (e: MouseEvent) => {
  if (!e.ctrlKey && backLocationEqualsHistoryBack.value) {
    window.history.back();
    e.preventDefault();
    e.stopPropagation();
  }
};
</script>
